/**
 * ProcureAI Unified Lifecycle Coordinator
 * Seamlessly coordinates the end-to-end corporate procurement lifecycle across all 7 stages:
 * Requisition (Employee) -> Approval (Manager) -> NFA/LFA (Approver) -> PO (Procurement) -> GRN (Warehouse) -> 3-Way Match Invoice (Finance AP) -> Payment (Finance Treasury).
 */

import { mockPurchaseRequestService } from './MockPurchaseRequestService';
import { mockApprovalService } from './MockApprovalService';
import { mockNfaLfaService } from './MockNfaLfaService';
import { mockPurchaseOrderService } from './MockPurchaseOrderService';
import { mockGoodsReceiptService } from './MockGoodsReceiptService';
import { mockInvoiceService } from './MockInvoiceService';
import { mockPaymentService } from './MockPaymentService';
import { mockPipelineService } from './MockPipelineService';
import { mockPrManagementService } from './MockPrManagementService';
import { mockRfqManagementService } from './MockRfqManagementService';
import { mockQuotationComparisonService } from './MockQuotationComparisonService';
import { mockSapSimulatorService } from './MockSapSimulatorService';
import { notifyGlobal } from '@/context/NotificationContext';
import { PurchaseRequest } from '@/types/domain/purchase-request';
import { NoteForApproval, LetterForAward } from '@/types/domain/nfa-lfa';
import { PrRecord } from '@/types/domain/pr-management';
import { RfqRecord } from '@/types/domain/rfq-management';
import { QuotationRecord } from '@/types/domain/quotation-comparison';
import { PurchaseOrderRecord } from '@/types/domain/po-management';
import { GoodsReceiptRecord } from '@/types/domain/grn-management';
import { InvoiceRecord } from '@/types/domain/invoice-management';
import { PaymentRecord } from '@/types/domain/payment-management';

export type LifecycleStageKey =
  | 'REQUEST'
  | 'MANAGER_APPROVAL'
  | 'SAP_PR_CREATED'
  | 'NFA_APPROVAL'
  | 'RFQ_PUBLISHED'
  | 'QUOTATIONS_RECEIVED'
  | 'PO_ISSUED'
  | 'GRN_ACCEPTED'
  | 'INVOICE_MATCHED'
  | 'PAYMENT_SETTLED';

export interface ConnectedProcurementChain {
  prId: string;
  requestNumber: string;
  title: string;
  totalAmount: number;
  currency: string;
  requesterName: string;
  departmentName: string;
  currentStage: LifecycleStageKey;
  stagesCompleted: number; // 1 to 12
  pr?: PurchaseRequest;
  sapPr?: PrRecord;
  nfa?: NoteForApproval;
  lfa?: LetterForAward;
  rfq?: RfqRecord;
  quotation?: QuotationRecord;
  po?: PurchaseOrderRecord;
  grn?: GoodsReceiptRecord;
  invoice?: InvoiceRecord;
  payment?: PaymentRecord;
}

class UnifiedLifecycleCoordinator {
  private chainCache = new Map<string, ConnectedProcurementChain>();

  constructor() {
    mockPurchaseRequestService.onRequestCreated(async (pr) => {
      await this.onRequisitionCreated(pr);
    });

    mockApprovalService.onApprovalChange(async (record, action, comments) => {
      if (action === 'STAGE_1_APPROVED') {
        if (record.approvalType === 'PURCHASE_REQUEST') {
          await this.onStage1ManagerSigned(record.id, comments);
        }
      } else if (action === 'APPROVED') {
        if (record.approvalType === 'PURCHASE_REQUEST') {
          await this.onManagerApproved(record.id, comments);
        } else if (record.approvalType === 'NFA') {
          await this.onNfaApproved(record.id, comments);
        }
      }
    });
  }

  /**
   * 1. Stage 1: Requisition Submitted (Employee / Requester Portal)
   */
  async onRequisitionCreated(pr: PurchaseRequest): Promise<ConnectedProcurementChain> {
    const seq = pr.requestNumber.replace(/[^0-9]/g, '') || String(Date.now()).slice(-4);

    // Sync into Approval Service
    mockApprovalService.syncPurchaseRequest(pr);

    // Sync into Pipeline Command Center
    mockPipelineService.syncPipelineRecord({
      id: pr.id,
      requestNumber: pr.requestNumber,
      title: pr.title,
      requesterId: pr.requesterId,
      requesterName: pr.requesterName,
      departmentId: pr.departmentId,
      departmentName: pr.departmentName,
      plantId: pr.plantId,
      plantName: pr.plantCode,
      companyCode: '1000',
      costCenter: pr.costCenterCode,
      category: pr.category,
      capexOpex: pr.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX',
      totalAmount: pr.totalAmount,
      currency: pr.currency,
      priority: pr.totalAmount > 50000 ? 'HIGH' : 'MEDIUM',
      createdDate: pr.createdAt,
      requiredDate: pr.requiredDate || '2026-10-30',
      expectedDeliveryDate: '2026-11-15',
      currentStage: 'APPROVAL',
      currentStatus: pr.status,
      isDelayed: false,
      delayDays: 0,
      isBlocked: false,
      connectedIds: {
        requestId: pr.id,
        sapPrNumber: `45000${seq}`,
      },
      stages: [
        { key: 'REQUEST', label: 'Requisition Created', status: 'COMPLETED', documentNumber: pr.requestNumber, completedDate: pr.createdAt },
        { key: 'APPROVAL', label: 'Department Manager Approval', status: 'IN_PROGRESS' },
        { key: 'SAP_PR', label: 'SAP PR Sync', status: 'NOT_STARTED' },
        { key: 'RFQ', label: 'RFQ Sourcing', status: 'NOT_STARTED' },
        { key: 'QUOTATION', label: 'Quotation Evaluation', status: 'NOT_STARTED' },
        { key: 'SUPPLIER', label: 'Supplier Selection', status: 'NOT_STARTED' },
        { key: 'PO', label: 'Purchase Order', status: 'NOT_STARTED' },
        { key: 'DELIVERY', label: 'Logistics Delivery', status: 'NOT_STARTED' },
        { key: 'GRN', label: 'Warehouse Goods Receipt', status: 'NOT_STARTED' },
        { key: 'INVOICE', label: '3-Way Match Invoice', status: 'NOT_STARTED' },
        { key: 'PAYMENT', label: 'Treasury Payment', status: 'NOT_STARTED' },
        { key: 'CLOSED', label: 'Procurement Closed', status: 'NOT_STARTED' },
      ],
      commentsCount: 1,
      source: 'PROCUREAI',
      version: 1,
    });

    const chain: ConnectedProcurementChain = {
      prId: pr.id,
      requestNumber: pr.requestNumber,
      title: pr.title,
      totalAmount: pr.totalAmount,
      currency: pr.currency,
      requesterName: pr.requesterName,
      departmentName: pr.departmentName,
      currentStage: 'REQUEST',
      stagesCompleted: 1,
      pr,
    };

    this.chainCache.set(pr.id, chain);
    this.chainCache.set(pr.requestNumber, chain);
    return chain;
  }

  /**
   * 2a. Stage 2: Manager Operational Signoff (Requisition routed to Finance for Budget Clearance)
   */
  async onStage1ManagerSigned(prId: string, comments?: string): Promise<ConnectedProcurementChain | null> {
    const pr = await mockPurchaseRequestService.getRequestById(prId);
    if (!pr) return null;

    pr.status = 'PendingApproval';
    if (pr.approvalChain && pr.approvalChain[0]) {
      pr.approvalChain[0].status = 'Approved';
      pr.approvalChain[0].actionDate = new Date().toISOString();
      pr.approvalChain[0].comments = comments || 'Approved by Department Manager';
    }
    if (pr.approvalChain && pr.approvalChain[1]) {
      pr.approvalChain[1].status = 'Pending';
    }

    mockPipelineService.syncPipelineRecord({
      id: pr.id,
      requestNumber: pr.requestNumber,
      title: pr.title,
      requesterId: pr.requesterId,
      requesterName: pr.requesterName,
      departmentId: pr.departmentId,
      departmentName: pr.departmentName,
      plantId: pr.plantId,
      plantName: pr.plantCode,
      companyCode: '1000',
      costCenter: pr.costCenterCode,
      category: pr.category,
      capexOpex: pr.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX',
      totalAmount: pr.totalAmount,
      currency: pr.currency,
      priority: pr.totalAmount > 50000 ? 'HIGH' : 'MEDIUM',
      createdDate: pr.createdAt,
      requiredDate: pr.requiredDate || '2026-10-30',
      expectedDeliveryDate: '2026-11-15',
      currentStage: 'APPROVAL',
      currentStatus: 'Pending Finance Clearance',
      isDelayed: false,
      delayDays: 0,
      isBlocked: false,
      connectedIds: {
        requestId: pr.id,
      },
      stages: [
        { key: 'REQUEST', label: 'Requisition Created', status: 'COMPLETED', documentNumber: pr.requestNumber, completedDate: pr.createdAt },
        { key: 'APPROVAL', label: 'Department Manager Approved — Pending Finance Budget Clearance', status: 'IN_PROGRESS' },
        { key: 'SAP_PR', label: 'SAP PR Sync', status: 'NOT_STARTED' },
        { key: 'RFQ', label: 'RFQ Sourcing', status: 'NOT_STARTED' },
        { key: 'QUOTATION', label: 'Quotation Evaluation', status: 'NOT_STARTED' },
        { key: 'SUPPLIER', label: 'Supplier Selection', status: 'NOT_STARTED' },
        { key: 'PO', label: 'Purchase Order', status: 'NOT_STARTED' },
        { key: 'DELIVERY', label: 'Logistics Delivery', status: 'NOT_STARTED' },
        { key: 'GRN', label: 'Warehouse Goods Receipt', status: 'NOT_STARTED' },
        { key: 'INVOICE', label: '3-Way Match Invoice', status: 'NOT_STARTED' },
        { key: 'PAYMENT', label: 'Treasury Payment', status: 'NOT_STARTED' },
        { key: 'CLOSED', label: 'Procurement Closed', status: 'NOT_STARTED' },
      ],
      commentsCount: 1,
      source: 'PROCUREAI',
      version: 2,
    });

    const chain = this.chainCache.get(pr.id) || this.chainCache.get(pr.requestNumber);
    if (chain) {
      chain.currentStage = 'MANAGER_APPROVAL';
      chain.stagesCompleted = 1;
      return chain;
    }
    return null;
  }

  /**
   * 2b. Stage 3: Finance Budget Clearance & SAP Inception (Finance Authorized -> Creates SAP PR & Sourcing RFQ)
   */
  async onManagerApproved(prId: string, comments?: string): Promise<ConnectedProcurementChain> {
    const pr = await mockPurchaseRequestService.getRequestById(prId);
    if (!pr) throw new Error(`Purchase Requisition ${prId} not found`);

    pr.status = 'Approved';
    if (pr.approvalChain && pr.approvalChain[0]) {
      pr.approvalChain[0].status = 'Approved';
      pr.approvalChain[0].actionDate = pr.approvalChain[0].actionDate || new Date().toISOString();
      pr.approvalChain[0].comments = pr.approvalChain[0].comments || 'Approved by Department Manager';
    }
    if (pr.approvalChain && pr.approvalChain[1]) {
      pr.approvalChain[1].status = 'Approved';
      pr.approvalChain[1].actionDate = new Date().toISOString();
      pr.approvalChain[1].comments = comments || 'Budget Cleared & Authorized by Finance Controller';
    }

    const seq = pr.requestNumber.replace(/[^0-9]/g, '') || String(Date.now()).slice(-4);
    const nfaNumber = `NFA-${seq}`;
    const nfaId = `nfa-dyn-${seq}`;

    // 2a. Automatic SAP PR Validation & Creation
    const sapPrRes = await mockSapSimulatorService.validateAndCreatePr({
      requestNumber: pr.requestNumber,
      companyCode: '1000',
      plantCode: pr.plantCode || '1000',
      costCenterCode: pr.costCenterCode || 'CC-IT-101',
      glAccountCode: '500100',
      materialCode: pr.items?.[0]?.materialCode || `MAT-${seq}`,
      materialDescription: (pr.items?.[0] as any)?.description || pr.items?.[0]?.materialName || pr.title,
      quantity: pr.items?.[0]?.quantity || 1,
      uom: pr.items?.[0]?.unitOfMeasure || (pr.items?.[0] as any)?.uom || 'EA',
      unitPrice: pr.items?.[0]?.estimatedUnitPrice || (pr.items?.[0] as any)?.unitPrice || pr.totalAmount,
      currency: pr.currency,
      deliveryDate: pr.requiredDate || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    });
    const sapPrNumber = sapPrRes.pr?.sapPrNumber || `45000${seq}`;

    // 2b. Sync into PR Management Service (PR Control Center /pr)
    const prRecord: PrRecord = {
      id: pr.id,
      prNumber: `PR-${seq}`,
      requestNumber: pr.requestNumber,
      title: pr.title,
      justification: pr.justification || 'Departmental operational requirement.',
      requesterId: pr.requesterId || 'usr-1',
      requesterName: pr.requesterName || 'Requester',
      departmentName: pr.departmentName,
      companyCode: '1000',
      plantCode: pr.plantCode || 'PLANT-1000',
      costCenterCode: pr.costCenterCode || 'CC-IT-101',
      purchasingOrg: '1000',
      purchasingGroup: 'PGRP-001',
      priority: pr.totalAmount > 50000 ? 'HIGH' : 'MEDIUM',
      status: 'SAP_CREATED',
      totalAmount: pr.totalAmount,
      currency: pr.currency,
      requiredDate: pr.requiredDate || '2026-10-30',
      items: pr.items && pr.items.length > 0 ? pr.items.map((it, idx) => ({
        id: `PR-ITEM-${seq}-${idx + 1}`,
        itemNumber: (idx + 1) * 10,
        materialCode: it.materialCode,
        description: it.materialName || (it as any).description || pr.title,
        category: pr.category,
        quantity: it.quantity,
        uom: it.unitOfMeasure || (it as any).uom || 'EA',
        unitPrice: it.estimatedUnitPrice || (it as any).unitPrice || 0,
        totalPrice: it.totalEstimatedPrice || (it as any).totalPrice || it.quantity * (it.estimatedUnitPrice || 0),
        currency: pr.currency,
        plantCode: pr.plantCode || 'PLANT-1000',
        deliveryDate: pr.requiredDate || '2026-10-30',
        costCenterCode: pr.costCenterCode || 'CC-IT-101',
        glAccountCode: '500100',
      })) : [
        {
          id: `PR-ITEM-${seq}-1`,
          itemNumber: 10,
          materialCode: `MAT-${seq}`,
          description: pr.title,
          category: pr.category,
          quantity: 1,
          uom: 'EA',
          unitPrice: pr.totalAmount,
          totalPrice: pr.totalAmount,
          currency: pr.currency,
          plantCode: pr.plantCode || 'PLANT-1000',
          deliveryDate: pr.requiredDate || '2026-10-30',
          costCenterCode: pr.costCenterCode || 'CC-IT-101',
          glAccountCode: '500100',
        },
      ],
      financialSummary: {
        subtotal: pr.totalAmount,
        taxAmount: Math.round(pr.totalAmount * 0.18),
        totalAmount: Math.round(pr.totalAmount * 1.18),
        currency: pr.currency,
        budgetLimit: Math.round(pr.totalAmount * 2),
        budgetConsumed: pr.totalAmount,
        availableBudget: pr.totalAmount,
        budgetStatus: 'SUFFICIENT',
      },
      sapValidation: {
        companyCode: true,
        plant: true,
        material: true,
        costCenter: true,
        glAccount: true,
        purchasingGroup: true,
        currency: true,
        quantity: true,
        deliveryDate: true,
        budget: true,
        isValid: true,
      },
      sapDetails: {
        sapPrNumber,
        sapStatus: 'CREATED',
        correlationId: sapPrRes.correlationId,
        createdTimestamp: new Date().toISOString(),
        lastSyncTimestamp: new Date().toISOString(),
        integrationStatus: 'SYNCED',
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
      lifecycle: [
        { key: 'CREATED', label: '1. Requisition Approved', status: 'COMPLETED', timestamp: new Date().toISOString().slice(0, 10) },
        { key: 'SAP_VALIDATED', label: '2. SAP BAPI Validated', status: 'COMPLETED', docRef: sapPrNumber, timestamp: new Date().toISOString().slice(0, 10) },
        { key: 'COMMERCIAL', label: '3. NFA Commercial Signoff', status: 'ACTIVE' },
        { key: 'SOURCING', label: '4. RFQ Sourcing', status: 'PENDING' },
      ],
    };
    mockPrManagementService.syncPrRecord(prRecord);

    // 2c. Sync into RFQ Management Service (Sourcing Center /rfq)
    const rfqId = `rfq-dyn-${seq}`;
    const rfqNumber = `RFQ-${seq}`;
    const rfqRecord: RfqRecord = {
      id: rfqId,
      rfqNumber,
      title: `Strategic Sourcing: ${pr.title}`,
      description: `Enterprise competitive RFQ for ${pr.title} under PR ${prRecord.prNumber}.`,
      category: pr.category,
      sourceRequestId: pr.requestNumber,
      sourcePRId: prRecord.id,
      sapPrNumber,
      procurementOwner: 'David Sterling',
      departmentName: pr.departmentName,
      companyCode: '1000',
      plantCode: pr.plantCode || 'PLANT-1000',
      purchasingOrg: '1000',
      purchasingGroup: 'PGRP-001',
      priority: pr.totalAmount > 50000 ? 'HIGH' : 'MEDIUM',
      status: 'RESPONSES_RECEIVED',
      estimatedTotalAmount: pr.totalAmount,
      currency: pr.currency,
      issueDate: new Date().toISOString(),
      closingDate: new Date(Date.now() + 14 * 86400000).toISOString(),
      timezone: 'UTC',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
      items: prRecord.items.map((it) => ({
        id: `RFQ-ITEM-${it.itemNumber}`,
        itemNumber: it.itemNumber,
        materialCode: it.materialCode,
        description: it.description,
        category: it.category,
        quantity: it.quantity,
        uom: it.uom,
        estimatedUnitPrice: it.unitPrice,
        totalEstimatedPrice: it.totalPrice,
        currency: it.currency,
        plantCode: it.plantCode,
        deliveryDate: it.deliveryDate,
      })),
      suppliers: [
        {
          id: `SUP-1-${seq}`,
          supplierId: 'VEND-101',
          supplierName: 'Dell Enterprise Solutions Ltd',
          category: pr.category,
          contactEmail: 'sales@dell.enterprise',
          invitationStatus: 'RESPONDED',
          invitedAt: new Date().toISOString(),
          respondedAt: new Date().toISOString(),
          quotationId: `quo-dyn-${seq}`,
          quotedAmount: pr.totalAmount,
          currency: pr.currency,
        },
        {
          id: `SUP-2-${seq}`,
          supplierId: 'VEND-102',
          supplierName: 'Lenovo Commercial Systems Ltd',
          category: pr.category,
          contactEmail: 'bids@lenovo.enterprise',
          invitationStatus: 'RESPONDED',
          invitedAt: new Date().toISOString(),
          respondedAt: new Date().toISOString(),
          quotationId: `quo-alt-${seq}`,
          quotedAmount: Math.round(pr.totalAmount * 1.05),
          currency: pr.currency,
        },
      ],
      commercialTerms: {
        paymentTerms: 'Net 30',
        incoterms: 'DDP',
        deliveryTerms: 'Express Freight to Receiving Dock',
        warrantyPeriod: '36 Months Onsite OEM Support',
        validityPeriod: '60 Days',
        freightTerms: 'Included in Quote',
      },
      lifecycle: [
        { key: 'CRT', label: '1. RFQ Created', status: 'COMPLETED', timestamp: new Date().toISOString().slice(0, 10) },
        { key: 'INV', label: '2. Suppliers Invited', status: 'COMPLETED', timestamp: new Date().toISOString().slice(0, 10) },
        { key: 'RSP', label: '3. Quotations Received', status: 'COMPLETED', timestamp: new Date().toISOString().slice(0, 10) },
        { key: 'EVL', label: '4. Evaluation Matrix Ready', status: 'ACTIVE' },
      ],
      quotationIds: [`quo-dyn-${seq}`, `quo-alt-${seq}`],
    };
    mockRfqManagementService.syncRfqRecord(rfqRecord);

    // 2d. Create Note For Approval (NFA)
    const nfa: NoteForApproval = {
      id: nfaId,
      nfaNumber,
      purchaseRequestId: pr.id,
      requestNumber: pr.requestNumber,
      title: `Commercial Authorization for ${pr.title}`,
      subject: `Note for Approval: Strategic Sourcing for ${pr.title}`,
      backgroundContext: pr.justification || 'Departmental procurement requirement.',
      commercialJustification: `Technical clearance authorized by ${pr.departmentName}. Price within acceptable budget variance.`,
      estimatedCost: pr.totalAmount,
      approvedCost: pr.totalAmount,
      currency: pr.currency,
      savingsEstimated: Math.round(pr.totalAmount * 0.08),
      budgetLineItem: pr.costCenterCode || 'CC-IT-101',
      costCenterCode: pr.costCenterCode || 'CC-IT-101',
      departmentCode: pr.departmentCode || 'DEP-IT',
      departmentName: pr.departmentName,
      plantCode: pr.plantCode || 'PLANT-1000',
      plantName: pr.plantCode || 'Plant 1000 — Mumbai Central Hub',
      capexOpex: pr.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX',
      procurementCategory: pr.category,
      status: 'UnderApproval',
      workflowType: 'NFA',
      approvalMode: 'SEQUENTIAL',
      matchedRule: {
        ruleId: 'rule-auto',
        ruleName: 'Auto Workflow Matrix',
        ruleVersion: '1.0',
        effectiveFrom: '2026-01-01',
        matchedConditions: [
          {
            condition: 'Total Value within budget delegation',
            passed: true,
            details: `Value: ${pr.totalAmount} ${pr.currency}`,
          },
        ],
      },
      delegations: [],
      clarifications: [],
      sla: {
        slaHours: 48,
        targetTimestamp: new Date(Date.now() + 48 * 3600000).toISOString(),
        remainingMinutes: 2880,
        status: 'OnTrack',
        reminderSent: false,
      },
      preparedByUserId: pr.requesterId || 'usr-101',
      preparedByName: pr.requesterName || 'Requester',
      preparedByRole: 'Department Lead',
      approvalChain: [
        {
          id: `stg-${seq}-1`,
          stageNumber: 1,
          stageName: 'Technical & Commercial Clearance',
          approverRole: 'Department Head',
          approverUserId: 'usr-102',
          approverName: 'Marcus Vance',
          approverEmail: 'marcus.vance@procureai.enterprise',
          approvalMode: 'SEQUENTIAL',
          status: 'Approved',
          isMandatory: true,
          actionDate: new Date().toISOString(),
          comments: comments || 'Approved',
        },
        {
          id: `stg-${seq}-2`,
          stageNumber: 2,
          stageName: 'Executive Steering Committee Signoff',
          approverRole: 'Executive Committee Lead',
          approverUserId: 'usr-106',
          approverName: 'Arthur Pendelton',
          approverEmail: 'approver@procure.com',
          approvalMode: 'SEQUENTIAL',
          status: 'InProgress',
          isMandatory: true,
        },
      ],
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    mockNfaLfaService.syncNfaRecord(nfa);

    // Register into Approval queue for NFA approver
    mockApprovalService.syncPurchaseRequest({
      id: nfa.id,
      requestNumber: nfa.nfaNumber,
      title: nfa.title,
      requesterId: pr.requesterId,
      requesterName: pr.requesterName,
      departmentId: pr.departmentId,
      departmentName: pr.departmentName,
      plantCode: pr.plantCode,
      costCenterCode: pr.costCenterCode,
      totalAmount: nfa.estimatedCost,
      currency: nfa.currency,
      category: pr.category,
      justification: nfa.commercialJustification,
      createdAt: nfa.createdAt,
      status: 'Pending',
      approvalType: 'NFA',
      items: pr.items,
    });

    // Update Pipeline
    mockPipelineService.syncPipelineRecord({
      id: pr.id,
      requestNumber: pr.requestNumber,
      title: pr.title,
      requesterId: pr.requesterId,
      requesterName: pr.requesterName,
      departmentId: pr.departmentId,
      departmentName: pr.departmentName,
      plantId: pr.plantId,
      plantName: pr.plantCode,
      companyCode: '1000',
      costCenter: pr.costCenterCode,
      category: pr.category,
      capexOpex: pr.category === 'CapitalEquipment' ? 'CAPEX' : 'OPEX',
      totalAmount: pr.totalAmount,
      currency: pr.currency,
      priority: pr.totalAmount > 50000 ? 'HIGH' : 'MEDIUM',
      createdDate: pr.createdAt,
      requiredDate: pr.requiredDate || '2026-10-30',
      expectedDeliveryDate: '2026-11-15',
      currentStage: 'RFQ',
      currentStatus: 'NFA / RFQ Sourcing in Progress',
      isDelayed: false,
      delayDays: 0,
      isBlocked: false,
      connectedIds: {
        requestId: pr.id,
        sapPrNumber: sapPrNumber,
        nfaId: nfa.nfaNumber,
        rfqId: rfqRecord.rfqNumber,
      },
      stages: [
        { key: 'REQUEST', label: 'Requisition Created', status: 'COMPLETED', documentNumber: pr.requestNumber, completedDate: pr.createdAt },
        { key: 'APPROVAL', label: 'Department Manager Approval', status: 'COMPLETED', completedDate: new Date().toISOString() },
        { key: 'SAP_PR', label: 'SAP PR Sync', status: 'COMPLETED', documentNumber: sapPrNumber },
        { key: 'RFQ', label: 'NFA / RFQ Sourcing', status: 'IN_PROGRESS', documentNumber: nfa.nfaNumber },
        { key: 'QUOTATION', label: 'Quotation Evaluation', status: 'NOT_STARTED' },
        { key: 'SUPPLIER', label: 'Supplier Selection', status: 'NOT_STARTED' },
        { key: 'PO', label: 'Purchase Order', status: 'NOT_STARTED' },
        { key: 'DELIVERY', label: 'Logistics Delivery', status: 'NOT_STARTED' },
        { key: 'GRN', label: 'Warehouse Goods Receipt', status: 'NOT_STARTED' },
        { key: 'INVOICE', label: '3-Way Match Invoice', status: 'NOT_STARTED' },
        { key: 'PAYMENT', label: 'Treasury Payment', status: 'NOT_STARTED' },
        { key: 'CLOSED', label: 'Procurement Closed', status: 'NOT_STARTED' },
      ],
    });

    notifyGlobal(
      'Manager Signoff Authorized',
      `Requisition ${pr.requestNumber} approved by Department Head. SAP PR ${sapPrNumber} validated and RFQ ${rfqRecord.rfqNumber} generated.`,
      'StatusChange',
      pr.id
    );

    const chain: ConnectedProcurementChain = {
      prId: pr.id,
      requestNumber: pr.requestNumber,
      title: pr.title,
      totalAmount: pr.totalAmount,
      currency: pr.currency,
      requesterName: pr.requesterName,
      departmentName: pr.departmentName,
      currentStage: 'NFA_APPROVAL',
      stagesCompleted: 2,
      pr,
      sapPr: prRecord,
      nfa,
      rfq: rfqRecord,
    };

    this.chainCache.set(pr.id, chain);
    return chain;
  }

  /**
   * 3. Stage 3: NFA / LFA Approved (NFA Approver Portal -> Spawns PO)
   */
  async onNfaApproved(nfaIdentifier: string, comments?: string): Promise<ConnectedProcurementChain> {
    const nfa = await mockNfaLfaService.getNfaById(nfaIdentifier);
    if (!nfa) throw new Error(`Note for Approval ${nfaIdentifier} not found`);

    nfa.status = 'Approved';
    if (nfa.approvalChain) {
      nfa.approvalChain.forEach((s) => {
        s.status = 'Approved';
        s.actionDate = new Date().toISOString();
        if (s.stageNumber === 2) s.comments = comments || 'Executive Steering Committee signoff authorized.';
      });
    }

    const seq = nfa.nfaNumber.replace(/[^0-9]/g, '') || String(Date.now()).slice(-4);
    const poNumber = `PO-${seq}`;
    const poId = `po-dyn-${seq}`;

    // Create Letter For Award (LFA)
    const lfa: LetterForAward = {
      id: `lfa-dyn-${seq}`,
      lfaNumber: `LFA-${seq}`,
      nfaId: nfa.id,
      nfaNumber: nfa.nfaNumber,
      purchaseRequestId: nfa.purchaseRequestId,
      requestNumber: nfa.requestNumber,
      title: nfa.title,
      rfqId: `rfq-${seq}`,
      rfqNumber: `RFQ-${seq}`,
      quotationId: `quo-${seq}`,
      vendorId: 'VEND-101',
      vendorName: 'Dell Enterprise Solutions Ltd',
      vendorCode: 'VND-DELL-01',
      awardedValue: nfa.estimatedCost,
      currency: nfa.currency,
      effectiveDate: new Date().toISOString().slice(0, 10),
      expiryDate: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
      paymentTerms: 'Net 30 Days from accepted Delivery Dock Inspection',
      deliveryTerms: 'Express Air / Secure Freight',
      status: 'Awarded',
      issuedByUserId: 'usr-106',
      issuedByName: 'Arthur Pendelton (Executive Committee)',
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    mockNfaLfaService.syncLfaRecord(lfa);

    // 3a. Ingest & sync winning quotation into Quotation Comparison Service
    const quoId = `quo-dyn-${seq}`;
    const quoNumber = `QUO-${seq}`;
    const winningQuotation: QuotationRecord = {
      id: quoId,
      quotationNumber: quoNumber,
      rfqId: `rfq-dyn-${seq}`,
      rfqNumber: `RFQ-${seq}`,
      vendorId: lfa.vendorId,
      vendorName: lfa.vendorName,
      vendorCode: lfa.vendorCode,
      sapVendorCode: 'SAP-VND-DELL',
      status: 'SELECTED',
      submittedAt: new Date().toISOString(),
      validUntil: new Date(Date.now() + 60 * 86400000).toISOString(),
      currency: nfa.currency,
      subtotalAmount: nfa.estimatedCost,
      discountAmount: Math.round(nfa.estimatedCost * 0.05),
      freightAmount: 0,
      taxAmount: Math.round(nfa.estimatedCost * 0.18),
      totalAmount: Math.round(nfa.estimatedCost * 1.13),
      evaluatedCost: nfa.estimatedCost,
      commercialTerms: {
        paymentTerms: 'Net 30 Days from accepted Delivery Dock Inspection',
        incoterms: 'DDP',
        warrantyPeriod: '36 Months',
        validityPeriod: '60 Days',
        freightTerms: 'Included in Quote',
        discountPercent: 5,
      },
      items: [
        {
          id: `QUO-ITEM-${seq}-1`,
          quotationId: quoId,
          rfqItemId: `RFQ-ITEM-10`,
          materialCode: 'MAT-IT-901',
          description: nfa.title,
          specification: 'Enterprise Standard Specification',
          quantity: 5,
          uom: 'EA',
          unitPrice: Math.round(nfa.estimatedCost / 5),
          discountAmount: 0,
          taxAmount: Math.round(nfa.estimatedCost * 0.18),
          freightAmount: 0,
          totalPrice: nfa.estimatedCost,
          deliveryDays: 14,
          technicalCompliance: 'COMPLIANT',
        },
      ],
      technicalScore: 98,
      commercialScore: 95,
      overallScore: 96.4,
      rank: 1,
      isSelected: true,
      selectionJustification: 'Lowest Total Cost of Ownership (TCO) with highest technical compliance and delivery reliability.',
    };
    mockQuotationComparisonService.syncQuotationRecord(winningQuotation);

    // 3b. Create Purchase Order (PO)
    const newPo = await mockPurchaseOrderService.createPurchaseOrder({
      vendorId: lfa.vendorId,
      vendorName: lfa.vendorName,
      vendorCode: lfa.vendorCode,
      companyCode: '1000',
      plant: nfa.plantCode || 'PLANT-1000',
      purchasingOrganization: 'PURCH-ORG-10',
      purchasingGroup: 'PG-01',
      currency: nfa.currency,
      requiredDate: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
      promisedDeliveryDate: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
      paymentTerms: 'Net 30',
      incoterms: 'DDP',
      deliveryTerms: 'Express Air / Secure Freight',
      shippingAddress: 'Plant 1000 - Receiving Dock 4, Mumbai Central Hub',
      billingAddress: 'Corporate Accounts Payable, ProcureAI Global HQ',
      submitForApproval: true,
      buyer: 'David Sterling',
      prId: nfa.purchaseRequestId,
      rfqId: `rfq-dyn-${seq}`,
      quotationId: winningQuotation.id,
      items: [
        {
          materialCode: 'MAT-IT-901',
          description: nfa.title,
          quantityOrdered: 5,
          unitPrice: Math.round(nfa.estimatedCost / 5),
          uom: 'EA',
          taxPercent: 18,
          deliveryDate: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
          plant: nfa.plantCode || 'PLANT-1000',
          storageLocation: 'WH-01',
          costCenter: nfa.costCenterCode || 'CC-1001',
          glAccount: '500100',
        },
      ],
    });

    // Explicitly link the generated PO IDs with the Requisition seq
    const originalPoId = newPo.id;
    newPo.id = poId;
    newPo.poNumber = poNumber;
    newPo.sourcePrNumber = nfa.requestNumber;
    newPo.prId = nfa.purchaseRequestId;
    newPo.status = 'SENT_TO_SUPPLIER';
    newPo.sapStatus = 'SYNCED';
    newPo.sapPoNumber = `4500${seq}`;
    mockPurchaseOrderService.syncPoRecord(newPo, originalPoId);

    // Update Pipeline
    mockPipelineService.syncPipelineRecord({
      id: nfa.purchaseRequestId,
      requestNumber: nfa.requestNumber,
      currentStage: 'PO',
      connectedIds: {
        requestId: nfa.purchaseRequestId,
        sapPrNumber: `45000${seq}`,
        nfaId: nfa.nfaNumber,
        rfqId: `RFQ-${seq}`,
        quotationId: winningQuotation.quotationNumber,
        poNumber: newPo.poNumber,
      },
    });

    notifyGlobal(
      'NFA Authorized & PO Issued',
      `Letter for Award ${lfa.lfaNumber} approved. Purchase Order ${newPo.poNumber} issued to ${lfa.vendorName}.`,
      'StatusChange',
      newPo.id
    );

    const chain = this.chainCache.get(nfa.purchaseRequestId) || {
      prId: nfa.purchaseRequestId,
      requestNumber: nfa.requestNumber,
      title: nfa.title,
      totalAmount: nfa.estimatedCost,
      currency: nfa.currency,
      requesterName: 'Requester',
      departmentName: nfa.departmentName,
      currentStage: 'PO_ISSUED' as const,
      stagesCompleted: 3,
    };

    chain.currentStage = 'PO_ISSUED';
    chain.stagesCompleted = 3;
    chain.nfa = nfa;
    chain.lfa = lfa;
    chain.quotation = winningQuotation;
    chain.po = newPo;
    this.chainCache.set(nfa.purchaseRequestId, chain);

    return chain;
  }

  /**
   * 4. Stage 4: Goods Receipt (Warehouse / Plant Dock -> Spawns GRN)
   */
  async onPoDelivered(poIdentifier: string): Promise<ConnectedProcurementChain> {
    const po = await mockPurchaseOrderService.getPurchaseOrderById(poIdentifier);
    if (!po) throw new Error(`Purchase Order ${poIdentifier} not found`);

    po.status = 'DELIVERED';
    po.threeWayMatchStatus = 'MATCHED';

    const seq = po.poNumber.replace(/[^0-9]/g, '') || String(Date.now()).slice(-4);
    const grnNumber = `GRN-${seq}`;
    const grnId = `grn-dyn-${seq}`;

    // Create Goods Receipt (GRN)
    const grn = await mockGoodsReceiptService.createGoodsReceipt({
      poId: po.id,
      poNumber: po.poNumber,
      sapPoNumber: po.sapPoNumber,
      vendorId: po.vendorId,
      vendorName: po.vendorName,
      vendorCode: po.vendorCode,
      plantCode: po.plant,
      plantName: 'Plant 1000 - Receiving Dock',
      storageLocation: 'SLOC-WH-01',
      unloadingDock: 'Dock Bay 3',
      deliveryChallanNumber: `DC-${seq}`,
      carrierName: 'DHL Global Forwarding',
      trackingNumber: `TRK-${seq}-99`,
      receivedDate: new Date().toISOString().slice(0, 10),
      inspectorName: 'Marcus Vance (Warehouse Lead)',
      items: po.items.map((it, idx) => ({
        lineNumber: it.lineNumber || (idx + 1),
        poItemId: it.id,
        materialCode: it.materialCode,
        description: it.description,
        orderedQuantity: it.quantityOrdered,
        receivedQuantity: it.quantityOrdered,
        rejectedQuantity: 0,
        uom: it.uom,
        storageLocation: it.storageLocation || 'SLOC-WH-01',
        unitPrice: it.unitPrice,
        taxPercent: it.taxPercent || 18,
      })),
    });

    const originalGrnId = grn.id;
    grn.id = grnId;
    grn.grnNumber = grnNumber;
    grn.status = 'ACCEPTED';
    grn.threeWayMatchStatus = 'MATCHED';
    mockGoodsReceiptService.syncGrnRecord(grn, originalGrnId);

    po.grnIds = [grn.id];

    // Update Pipeline
    if (po.prId) {
      mockPipelineService.syncPipelineRecord({
        id: po.prId,
        requestNumber: po.sourcePrNumber,
        currentStage: 'GRN',
        connectedIds: {
          requestId: po.prId,
          poNumber: po.poNumber,
          grnNumber: grn.grnNumber,
        },
      });
    }

    notifyGlobal(
      'Goods Receipt Accepted',
      `Shipment for PO ${po.poNumber} received and verified at Dock (${grn.grnNumber}).`,
      'StatusChange',
      grn.id
    );

    const chain: ConnectedProcurementChain = this.chainCache.get(po.prId) || {
      prId: po.prId,
      requestNumber: po.sourcePrNumber || po.prId,
      title: po.notes || 'Procurement Item',
      totalAmount: po.totalValue,
      currency: po.currency,
      requesterName: 'Requester',
      departmentName: 'Operations',
      currentStage: 'GRN_ACCEPTED' as const,
      stagesCompleted: 4,
    };

    chain.currentStage = 'GRN_ACCEPTED';
    chain.stagesCompleted = 4;
    chain.po = po;
    chain.grn = grn;
    this.chainCache.set(po.prId, chain);

    return chain;
  }

  /**
   * 5. Stage 5: Invoice Ingested & 3-Way Matched (Finance AP)
   */
  async onGrnInvoiceMatched(grnIdentifier: string): Promise<ConnectedProcurementChain> {
    const grn = await mockGoodsReceiptService.getGoodsReceiptById(grnIdentifier);
    if (!grn) throw new Error(`Goods Receipt ${grnIdentifier} not found`);

    const po = await mockPurchaseOrderService.getPurchaseOrderById(grn.poId);
    if (!po) throw new Error(`Associated PO ${grn.poId} not found`);

    const seq = grn.grnNumber.replace(/[^0-9]/g, '') || String(Date.now()).slice(-4);
    const invNumber = `INV-${seq}`;
    const invId = `inv-dyn-${seq}`;

    // Create Invoice with 3-Way Match Passed
    const invoice = await mockInvoiceService.createInvoice({
      vendorInvoiceNumber: `VND-INV-${seq}`,
      poId: po.id,
      poNumber: po.poNumber,
      grnId: grn.id,
      grnNumber: grn.grnNumber,
      vendorId: po.vendorId,
      vendorName: po.vendorName,
      vendorCode: po.vendorCode,
      currency: po.currency,
      invoiceDate: new Date().toISOString().slice(0, 10),
      receivedDate: new Date().toISOString().slice(0, 10),
      dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      paymentTerms: 'Net 30',
      shippingAmount: 0,
      otherCharges: 0,
      discountAmount: 0,
      items: grn.items.map((it, idx) => ({
        lineNumber: it.lineNumber || (idx + 1),
        poItemId: it.poItemId,
        materialCode: it.materialCode,
        description: it.description || 'Procurement Item',
        quantity: it.acceptedQuantity,
        uom: it.uom,
        unitPrice: it.unitPrice,
        taxRate: 18,
      })),
    });

    const originalInvId = invoice.id;
    invoice.id = invId;
    invoice.invoiceNumber = invNumber;
    invoice.status = 'APPROVED';
    invoice.matchingStatus = 'MATCHED';
    invoice.paymentStatus = 'READY_FOR_PAYMENT';
    invoice.isReadyForPayment = true;
    invoice.sapSyncStatus = 'SYNCED';
    invoice.sapMiroNumber = `510090${seq}`;
    mockInvoiceService.syncInvoiceRecord(invoice, originalInvId);

    po.invoiceIds = [invoice.id];

    // Update Pipeline
    if (po.prId) {
      mockPipelineService.syncPipelineRecord({
        id: po.prId,
        requestNumber: po.sourcePrNumber,
        currentStage: 'INVOICE',
        threeWayMatch: {
          matchStatus: 'MATCHED',
          priceVariance: 0,
          quantityVariance: 0,
          poAmount: po.totalValue,
          grnAmount: po.totalValue,
          invoiceAmount: invoice.totalAmount,
        },
        connectedIds: {
          requestId: po.prId,
          poNumber: po.poNumber,
          grnNumber: grn.grnNumber,
          invoiceNumber: invoice.invoiceNumber,
        },
      });
    }

    notifyGlobal(
      'Invoice 3-Way Matched',
      `Invoice ${invoice.invoiceNumber} verified against PO & GRN. Ready for payment disbursement.`,
      'StatusChange',
      invoice.id
    );

    const chain: ConnectedProcurementChain = this.chainCache.get(po.prId) || {
      prId: po.prId,
      requestNumber: po.sourcePrNumber || po.prId,
      title: po.notes || 'Procurement Item',
      totalAmount: po.totalValue,
      currency: po.currency,
      requesterName: 'Requester',
      departmentName: 'Finance AP',
      currentStage: 'INVOICE_MATCHED' as const,
      stagesCompleted: 5,
    };

    chain.currentStage = 'INVOICE_MATCHED';
    chain.stagesCompleted = 5;
    chain.po = po;
    chain.grn = grn;
    chain.invoice = invoice;
    this.chainCache.set(po.prId, chain);

    return chain;
  }

  /**
   * 6. Stage 6: Treasury Payment Execution (Finance Treasury -> Final Settlement)
   */
  async onPaymentDisbursed(invoiceIdentifier: string): Promise<ConnectedProcurementChain> {
    const inv = await mockInvoiceService.getInvoiceById(invoiceIdentifier);
    if (!inv) throw new Error(`Invoice ${invoiceIdentifier} not found`);

    const seq = inv.invoiceNumber.replace(/[^0-9]/g, '') || String(Date.now()).slice(-4);
    const payId = `pay-dyn-${seq}`;
    const payRef = `PAY-${seq}`;

    // Create & Settle Payment
    const payment = await mockPaymentService.createPaymentInstruction({
      invoiceId: inv.id,
      amount: inv.totalAmount,
      paymentMethod: 'NEFT',
      paymentType: 'FULL',
      notes: 'Final settlement after authorized 3-way match approval.',
    });

    payment.id = payId;
    payment.paymentReference = payRef;
    payment.status = 'PAID';
    payment.paidAmount = inv.totalAmount;
    payment.remainingAmount = 0;
    payment.sapSyncStatus = 'SYNCED';
    payment.sapDocumentNumber = `150000${seq}`;
    mockPaymentService.syncPaymentRecord(payment);

    inv.paymentStatus = 'PAID';

    // Find linked PO
    const po = await mockPurchaseOrderService.getPurchaseOrderById(inv.poId);
    if (po) {
      po.paymentIds = [payment.id];
      po.status = 'CLOSED';

      if (po.prId) {
        mockPipelineService.syncPipelineRecord({
          id: po.prId,
          requestNumber: po.sourcePrNumber,
          currentStage: 'CLOSED',
          connectedIds: {
            requestId: po.prId,
            poNumber: po.poNumber,
            invoiceNumber: inv.invoiceNumber,
            paymentId: payment.paymentReference,
          },
          stages: [
            { key: 'REQUEST', label: 'Requisition Created', status: 'COMPLETED' },
            { key: 'APPROVAL', label: 'Department Manager Approval', status: 'COMPLETED' },
            { key: 'SAP_PR', label: 'SAP PR Sync', status: 'COMPLETED' },
            { key: 'RFQ', label: 'NFA / RFQ Sourcing', status: 'COMPLETED' },
            { key: 'QUOTATION', label: 'Quotation Evaluation', status: 'COMPLETED' },
            { key: 'SUPPLIER', label: 'Supplier Selection', status: 'COMPLETED' },
            { key: 'PO', label: 'Purchase Order', status: 'COMPLETED', documentNumber: po.poNumber },
            { key: 'DELIVERY', label: 'Logistics Delivery', status: 'COMPLETED' },
            { key: 'GRN', label: 'Warehouse Goods Receipt', status: 'COMPLETED', documentNumber: inv.grnNumber },
            { key: 'INVOICE', label: '3-Way Match Invoice', status: 'COMPLETED', documentNumber: inv.invoiceNumber },
            { key: 'PAYMENT', label: 'Treasury Payment', status: 'COMPLETED', documentNumber: payment.paymentReference },
            { key: 'CLOSED', label: 'Procurement Closed', status: 'COMPLETED', completedDate: new Date().toISOString() },
          ],
        });
      }
    }

    notifyGlobal(
      'Payment Settled & Cleared',
      `Payment voucher ${payment.paymentReference} settled via NEFT. SAP Financial doc #${payment.sapDocumentNumber} registered.`,
      'System',
      payment.id
    );

    const chain = this.chainCache.get(po?.prId || '') || {
      prId: po?.prId || 'req-1',
      requestNumber: po?.sourcePrNumber || 'REQ-1',
      title: inv.vendorName,
      totalAmount: inv.totalAmount,
      currency: inv.currency,
      requesterName: 'Requester',
      departmentName: 'Treasury',
      currentStage: 'PAYMENT_SETTLED' as const,
      stagesCompleted: 7,
    };

    chain.currentStage = 'PAYMENT_SETTLED';
    chain.stagesCompleted = 7;
    chain.invoice = inv;
    chain.payment = payment;
    if (po) this.chainCache.set(po.prId, chain);

    return chain;
  }

  /**
   * Fast-Forward Demo: Automatically advances the entire procurement chain till Payment in 1 click!
   */
  async simulateFullFlow(prId: string): Promise<ConnectedProcurementChain> {
    // 1. Manager Approval -> Spawns NFA
    const step2 = await this.onManagerApproved(prId, 'Executive fast-track authorization');

    // 2. NFA Approval -> Spawns PO & LFA
    const step3 = await this.onNfaApproved(step2.nfa!.id, 'Executive Steering Committee unanimous clearance');

    // 3. PO Delivery -> Spawns GRN
    const step4 = await this.onPoDelivered(step3.po!.id);

    // 4. GRN Acceptance -> Spawns Invoice with 3-Way Match
    const step5 = await this.onGrnInvoiceMatched(step4.grn!.id);

    // 5. Payment Disbursement -> Settled & SAP Synced
    const step6 = await this.onPaymentDisbursed(step5.invoice!.id);

    notifyGlobal(
      'Full Procurement Flow Simulated',
      `Requisition ${step2.requestNumber} progressed through PR -> Manager -> NFA -> PO -> GRN -> Invoice -> Payment!`,
      'StatusChange',
      prId
    );

    return step6;
  }

  /**
   * Retrieves the connected procurement chain for any Requisition ID or number.
   */
  async getChainForRequisition(identifier: string): Promise<ConnectedProcurementChain | null> {
    const cached = this.chainCache.get(identifier);
    if (cached) return cached;

    // Dynamically reconstruct chain from repositories if not cached
    const pr = await mockPurchaseRequestService.getRequestById(identifier);
    if (!pr) return null;

    const seq = pr.requestNumber.replace(/[^0-9]/g, '') || pr.id.replace(/[^0-9]/g, '');

    const prRecords = await mockPrManagementService.getPrs({ pageSize: 500 });
    const sapPr = prRecords.items.find(
      (p) => p.id === pr.id || p.requestNumber === pr.requestNumber || p.prNumber.includes(seq)
    );

    const rfqRecords = await mockRfqManagementService.getRfqs({ pageSize: 500 });
    const rfqItems = rfqRecords.items || (rfqRecords as any).data || [];
    const rfq = rfqItems.find(
      (r: any) => r.sourcePRId === pr.id || r.sourceRequestId === pr.requestNumber || r.rfqNumber?.includes(seq)
    );

    const quoteRecords = await mockQuotationComparisonService.getQuotations({ pageSize: 500 });
    const quoteItems = quoteRecords.items || (quoteRecords as any).data || [];
    const quotation = quoteItems.find(
      (q: any) => (rfq && q.rfqId === rfq.id) || q.rfqNumber?.includes(seq) || q.quotationNumber?.includes(seq)
    );

    const nfas = await mockNfaLfaService.getNfas({ pageSize: 500 });
    const nfa = nfas.nfas.find(
      (n) => n.purchaseRequestId === pr.id || n.requestNumber === pr.requestNumber || n.nfaNumber.includes(seq)
    );

    const lfas = await mockNfaLfaService.getLfas();
    const lfa = lfas.find(
      (l) => l.purchaseRequestId === pr.id || l.requestNumber === pr.requestNumber || l.lfaNumber.includes(seq)
    );

    const pos = await mockPurchaseOrderService.getPurchaseOrders({ pageSize: 500 });
    const po = pos.items.find(
      (p) => p.prId === pr.id || p.sourcePrNumber === pr.requestNumber || p.poNumber.includes(seq)
    );

    const grns = await mockGoodsReceiptService.getGoodsReceipts({ pageSize: 500 } as any);
    const grn = grns.items.find((g) => (po && g.poId === po.id) || g.grnNumber.includes(seq));

    const invs = await mockInvoiceService.getInvoices({ pageSize: 500 } as any);
    const invoice = invs.items.find((i) => (po && i.poId === po.id) || i.invoiceNumber.includes(seq));

    const pays = await mockPaymentService.getPayments({ pageSize: 500 } as any);
    const payment = pays.items.find((p) => (invoice && p.invoiceId === invoice.id) || p.paymentReference.includes(seq));

    let stagesCompleted = 1;
    let currentStage: LifecycleStageKey = 'REQUEST';

    if (payment && payment.status === 'PAID') {
      stagesCompleted = 7;
      currentStage = 'PAYMENT_SETTLED';
    } else if (invoice) {
      stagesCompleted = 5;
      currentStage = 'INVOICE_MATCHED';
    } else if (grn) {
      stagesCompleted = 4;
      currentStage = 'GRN_ACCEPTED';
    } else if (po) {
      stagesCompleted = 3;
      currentStage = 'PO_ISSUED';
    } else if (nfa) {
      stagesCompleted = 2;
      currentStage = 'NFA_APPROVAL';
    } else if (pr.status === 'Approved') {
      stagesCompleted = 2;
      currentStage = 'NFA_APPROVAL';
    }

    const reconstructed: ConnectedProcurementChain = {
      prId: pr.id,
      requestNumber: pr.requestNumber,
      title: pr.title,
      totalAmount: pr.totalAmount,
      currency: pr.currency,
      requesterName: pr.requesterName,
      departmentName: pr.departmentName,
      currentStage,
      stagesCompleted,
      pr,
      sapPr,
      nfa,
      lfa,
      rfq,
      quotation,
      po,
      grn,
      invoice,
      payment,
    };

    this.chainCache.set(pr.id, reconstructed);
    this.chainCache.set(pr.requestNumber, reconstructed);
    return reconstructed;
  }
}

export const unifiedLifecycleCoordinator = new UnifiedLifecycleCoordinator();
